import { z } from "zod";
import { MediaFile } from "./media.ts";

/**
 * Картинка, брошенная на доску урока. Проект целиком:
 * `docs/room-feedback-fixes.md`, раздел 2.
 *
 * Суть решения в одной строке: **байты уезжают в медиатеку преподавателя, а в
 * `Y.Doc` едет только идентификатор файла.** Base64 внутрь документа класть
 * нельзя ни при каких условиях: скриншот это сотни килобайт против
 * 16-килобайтной отсечки (раздел 3 CLAUDE.md), плюс вечные надгробия при
 * удалении картинки (раздел 5.3 CLAUDE.md, yjs#741).
 *
 * Здесь живут числа и форма ответа, то есть ровно то, что обязаны одинаково
 * знать обе стороны: клиент ужимает картинку до отправки, сервер проверяет
 * результат. Разъехавшиеся числа означали бы картинку, принятую браузером и
 * отвергнутую сервером после отправки по мобильному каналу.
 *
 * Отдельным файлом, а не строками в `./media.ts`: тот и так 550 строк, а это
 * не «ещё немного медиатеки», а свой контракт со своими правилами.
 */

/**
 * До скольких пикселей по длинной стороне ужимает КЛИЕНТ.
 *
 * Рукописный текст и скан воркшита на 2000 px читаются, а доска всё равно живёт
 * в логических единицах слайда: двенадцати мегапикселей там не видит никто.
 */
export const BOARD_IMAGE_MAX_EDGE = 2000;

/**
 * Потолок на то, что приезжает на сервер. Страховка, а не рычаг: после ужатия
 * сюда не влезает только ненормальное.
 *
 * ⚠️ **Отказ по размеру это неправильный рычаг, правильный это ужатие.** Порог
 * в 1.5 МБ, обсуждавшийся первым, отсекал бы самый частый честный случай:
 * фотографию тетради с телефона. Ученик, сфотографировавший задачу, получал бы
 * отказ в худший момент из возможных (раздел 2.3 проекта).
 */
export const BOARD_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Потолок на ИСХОДНЫЙ файл, который клиент берётся ужать. Фотография с телефона
 * это 3-5 МБ, и её надо принять и ужать, а не отвергнуть.
 *
 * Сервер этого числа не видит вовсе: до него доезжает уже ужатое. Живёт оно
 * здесь затем, что это часть одного и того же правила, и второе место, где его
 * можно было бы задать, однажды разошлось бы с первым.
 */
export const BOARD_IMAGE_SOURCE_MAX_BYTES = 20 * 1024 * 1024;

/**
 * Сколько картинок за урок кладёт УЧЕНИК. Накопительно, без возврата при
 * удалении: так проще и не обходится через «вставил, удалил, вставил».
 *
 * У преподавателя счётчика по числу нет вовсе, его держит собственная квота на
 * 5 ГБ: пятнадцать картинок с заданиями от преподавателя это работа, пятнадцать
 * от ученика это шум.
 */
export const BOARD_IMAGE_STUDENT_LIMIT = 10;

/**
 * Что принимаем на доску. Список закрытый и УЖЕ клиентской медиатеки: SVG нам
 * сюда не нужен (его никто не рисует в браузере ужатием канваса), а всё
 * остальное решается сигнатурой на сервере.
 */
export const BOARD_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type BoardImageType = (typeof BOARD_IMAGE_TYPES)[number];

export function isBoardImageType(contentType: string): contentType is BoardImageType {
  return (BOARD_IMAGE_TYPES as readonly string[]).includes(contentType);
}

/**
 * Сколько картинок ученику ещё можно.
 *
 * Чистая функция с зажимом снизу, а не вычитание на месте: счётчик
 * накопительный, а потолок однажды станет настраиваемым (раздел 2.4 проекта:
 * преподаватель разрешает добавить ещё), и «минус два» на экране это то, что
 * получается из вычитания, сделанного в четырёх местах по-разному.
 */
export function boardImagesLeft(used: number, limit: number = BOARD_IMAGE_STUDENT_LIMIT): number {
  return Math.max(0, limit - Math.max(0, used));
}

/**
 * Ответ узкой ручки загрузки.
 *
 * `studentRemaining` это `null` у преподавателя, и это не «неизвестно», а «его
 * не считают по числу». Ноль означал бы «больше нельзя», то есть ровно
 * противоположное.
 */
export const BoardImageResponse = z.object({
  file: MediaFile,
  studentRemaining: z.number().int().nonnegative().nullable().default(null),
});
export type BoardImageResponse = z.infer<typeof BoardImageResponse>;
